import React, { useState, Component } from 'react';
import { CheckCircle2, Loader2, Plus, Trash2 } from 'lucide-react';
const BRIDGE_URL = 'https://lynjones-monitor-382427532499.us-central1.run.app/webhook/new-contact';
const EQUIPMENT_TYPES = ['Tractor', 'Excavator', 'Bulldozer', 'Backhoe', 'Skid Steer', 'Wheel Loader', 'Forklift', 'Crane', 'Combine / Harvester', 'Telehandler', 'Other Heavy Equipment'];
const CONDITION_OPTS = ['Operable', 'Inoperable', 'Unsure'];
type Vehicle = {
  type: string;
  year: string;
  make: string;
  weight: string;
  condition: string;
};
const emptyVehicle = (): Vehicle => ({
  type: '',
  year: '',
  make: '',
  weight: '',
  condition: 'Operable'
});
function getGclid(): string {
  try {
    const m = new RegExp('[?&]gclid=([^&]*)').exec(window.location.search);
    if (m && m[1]) {
      const g = decodeURIComponent(m[1].replace(/\+/g, ' '));
      try {
        localStorage.setItem('lj_gclid', g);
      } catch (e) {}
      return g;
    }
    try {
      return localStorage.getItem('lj_gclid') || '';
    } catch (e) {
      return '';
    }
  } catch (e) {
    return '';
  }
}
export function LeadForm({
  title = 'Get Your Free Heavy Equipment Transport Quote',
  source = 'Heavy Haul PPC'
}: {
  title?: string;
  source?: string;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    fullName: '',
    phone: '',
    email: '',
    pickup: '',
    delivery: '',
    shipDate: ''
  });
  const [vehicles, setVehicles] = useState<Vehicle[]>([emptyVehicle()]);
  const update = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm({
    ...form,
    [field]: e.target.value
  });
  const updateVehicle = (idx: number, field: keyof Vehicle, value: string) => {
    setVehicles(prev => prev.map((v, i) => i === idx ? {
      ...v,
      [field]: value
    } : v));
  };
  const addVehicle = () => setVehicles(prev => prev.length >= 5 ? prev : [...prev, emptyVehicle()]);
  const removeVehicle = (idx: number) => setVehicles(prev => prev.filter((_, i) => i !== idx));
  const describeVehicle = (v: Vehicle) => [v.year, v.make, v.type, v.weight ? `${v.weight}lbs` : '', v.condition !== 'Operable' ? `(${v.condition})` : ''].filter(Boolean).join(' ').trim();
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    const first = vehicles[0] || emptyVehicle();
    const allDesc = vehicles.map(describeVehicle).filter(Boolean).join(' | ');
    const comment = ('Equipment: ' + allDesc + ' | Pickup ' + form.pickup + ' to Delivery ' + form.delivery + ' | Ship ' + form.shipDate).slice(0, 480);
    const payload = {
      name: form.fullName,
      email: form.email,
      phone: form.phone,
      source,
      tags: ['request a quote', 'heavy haul ppc'],
      origin_zip: form.pickup,
      destination_zip: form.delivery,
      ship_date: form.shipDate,
      // BatCRM-compatible primary vehicle
      vehicle_type: 'Heavy Equipment',
      vehicle_make: first.type || 'Heavy Equipment',
      vehicle_model: [first.make, first.weight ? `${first.weight}lbs` : ''].filter(Boolean).join(' ') || first.type || 'Equipment',
      vehicle_model_year: first.year,
      vehicle_inop: first.condition === 'Operable' ? 'no' : 'yes',
      transport_type: 1,
      // Readable multi-equipment detail
      vehicle: allDesc,
      vehicle_count: String(vehicles.length),
      comment_from_shipper: comment,
      comments: comment,
      gclid: getGclid(),
      do_bats: true,
      do_email: true
    };
    try {
      const res = await fetch(BRIDGE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      setIsSuccess(true);
    } catch (err) {
      setError('Sorry, something went wrong. Please call us at (754) 325-1100 and we will get your quote started right away.');
    } finally {
      setIsSubmitting(false);
    }
  };
  if (isSuccess) {
    return <div className="bg-white p-8 rounded-xl shadow-xl border border-slate-100 flex flex-col items-center justify-center text-center h-full min-h-[400px]" data-id="element-185">
        <CheckCircle2 className="w-16 h-16 text-green-500 mb-4" data-id="element-186" />
        <h3 className="text-2xl font-bold text-brand-navy mb-2" data-id="element-187">
          Quote Request Received!
        </h3>
        <p className="text-slate-600 mb-6" data-id="element-188">
          Thank you. One of our heavy equipment transport specialists will
          contact you shortly with your free, no-obligation quote.
        </p>
        <button onClick={() => {
        setIsSuccess(false);
        setForm({
          fullName: '',
          phone: '',
          email: '',
          pickup: '',
          delivery: '',
          shipDate: ''
        });
        setVehicles([emptyVehicle()]);
      }} className="text-brand-navy font-semibold hover:text-brand-red transition-colors" data-id="element-189">
          Submit another request
        </button>
      </div>;
  }
  const inputCls = 'w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-brand-navy focus:border-brand-navy outline-none transition-all';
  const labelCls = 'block text-sm font-semibold text-slate-700 mb-1';
  return <div className="bg-white p-6 md:p-8 rounded-xl shadow-xl border border-slate-100 w-full max-w-lg mx-auto" id="quote-form" data-id="element-190">
      <h3 className="text-2xl font-bold text-brand-navy mb-2 text-center" data-id="element-191">
        {title}
      </h3>
      <p className="text-sm text-slate-500 text-center mb-6 font-medium" data-id="element-192">
        No upfront payment • 100% free quote
      </p>

      <form onSubmit={handleSubmit} className="space-y-4" data-id="element-193">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-194">
          <div data-id="element-195">
            <label htmlFor="fullName" className={labelCls} data-id="element-196">
              Full Name
            </label>
            <input required value={form.fullName} onChange={update('fullName')} type="text" id="fullName" className={inputCls} placeholder="John Doe" data-id="element-197" />
          </div>
          <div data-id="element-198">
            <label htmlFor="phone" className={labelCls} data-id="element-199">
              Phone Number
            </label>
            <input required value={form.phone} onChange={update('phone')} type="tel" id="phone" className={inputCls} placeholder="(555) 123-4567" data-id="element-200" />
          </div>
        </div>

        <div data-id="element-201">
          <label htmlFor="email" className={labelCls} data-id="element-202">
            Email Address
          </label>
          <input required value={form.email} onChange={update('email')} type="email" id="email" className={inputCls} placeholder="john@example.com" data-id="element-203" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-204">
          <div data-id="element-205">
            <label htmlFor="pickup" className={labelCls} data-id="element-206">
              Pickup ZIP Code
            </label>
            <input required value={form.pickup} onChange={update('pickup')} type="text" inputMode="numeric" id="pickup" className={inputCls} placeholder="e.g. 33301" data-id="element-207" />
          </div>
          <div data-id="element-208">
            <label htmlFor="delivery" className={labelCls} data-id="element-209">
              Delivery ZIP Code
            </label>
            <input required value={form.delivery} onChange={update('delivery')} type="text" inputMode="numeric" id="delivery" className={inputCls} placeholder="e.g. 89101" data-id="element-210" />
          </div>
        </div>

        {/* Dynamic equipment rows */}
        <div className="space-y-4 pt-1" data-id="element-211">
          <label className={labelCls} data-id="element-212">Your Equipment</label>
          {vehicles.map((v, idx) => <div key={idx} className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3" data-id="element-213">
              <div className="flex items-center justify-between" data-id="element-214">
                <span className="text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-215">
                  Equipment #{idx + 1}
                </span>
                {vehicles.length > 1 && <button type="button" onClick={() => removeVehicle(idx)} className="text-slate-400 hover:text-brand-red transition-colors" aria-label="Remove equipment" data-id="element-216">
                    <Trash2 className="w-4 h-4" data-id="element-217" />
                  </button>}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-218">
                <select required value={v.type} onChange={e => updateVehicle(idx, 'type', e.target.value)} className={inputCls} data-id="element-219">
                  <option value="" disabled data-id="element-220">
                    Equipment Type...
                  </option>
                  {EQUIPMENT_TYPES.map(t => <option key={t} value={t} data-id="element-221">
                      {t}
                    </option>)}
                </select>
                <select value={v.condition} onChange={e => updateVehicle(idx, 'condition', e.target.value)} className={inputCls} data-id="element-222">
                  {CONDITION_OPTS.map(t => <option key={t} value={t} data-id="element-223">
                      {t}
                    </option>)}
                </select>
              </div>
              <div className="grid grid-cols-3 gap-3" data-id="element-224">
                <input value={v.year} onChange={e => updateVehicle(idx, 'year', e.target.value)} type="text" inputMode="numeric" className={inputCls} placeholder="Year" data-id="element-225" />
                <input value={v.make} onChange={e => updateVehicle(idx, 'make', e.target.value)} type="text" className={inputCls} placeholder="Make/Model" data-id="element-226" />
                <input value={v.weight} onChange={e => updateVehicle(idx, 'weight', e.target.value)} type="text" inputMode="numeric" className={inputCls} placeholder="Weight (lbs)" data-id="element-227" />
              </div>
            </div>)}
          {vehicles.length < 5 && <button type="button" onClick={addVehicle} className="flex items-center text-sm font-semibold text-brand-navy hover:text-brand-red transition-colors" data-id="element-228">
              <Plus className="w-4 h-4 mr-1" data-id="element-229" /> Add another piece of equipment
            </button>}
        </div>

        <div data-id="element-230">
          <label htmlFor="shipDate" className={labelCls} data-id="element-231">
            Preferred Ship Date
          </label>
          <input required value={form.shipDate} onChange={update('shipDate')} type="date" id="shipDate" className={inputCls + ' text-slate-700'} data-id="element-232" />
        </div>

        {error && <p className="text-sm text-brand-red font-medium text-center" data-id="element-233">
            {error}
          </p>}

        <button type="submit" disabled={isSubmitting} className="w-full bg-brand-red hover:bg-brand-red-hover text-white font-bold py-4 px-6 rounded-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center text-lg mt-2 disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-234">
          {isSubmitting ? <>
              <Loader2 className="w-6 h-6 mr-2 animate-spin" data-id="element-235" />
              Processing...
            </> : 'Get My Free Quote'}
        </button>
        <p className="text-xs text-slate-500 text-center mt-3" data-id="element-236">
          By submitting, you agree to our terms. Your information is secure.
        </p>
      </form>
    </div>;
}